import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { franquiaService } from '../../services/franquiaService';
import { Franquia } from '../../types/franquia';

interface FranquiaDetalhesFormProps {
  franquia: Franquia;
  franquiaId: number;
}

// Define um tipo para o corpo da requisição PATCH
type FranquiaPatchData = {
  razao_social?: string;
  nome_fantasia?: string;
  limite_funcionarios?: number;
};

export const FranquiaDetalhesForm: React.FC<FranquiaDetalhesFormProps> = ({ 
  franquia, 
  franquiaId 
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    razao_social: franquia?.razao_social || '',
    nome_fantasia: franquia?.nome_fantasia || '',
    limite_funcionarios: franquia?.limite_funcionarios || 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (franquia) {
      setFormData({
        razao_social: franquia.razao_social,
        nome_fantasia: franquia.nome_fantasia,
        limite_funcionarios: franquia.limite_funcionarios,
      });
    }
  }, [franquia]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value, 10) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Cria o objeto de patch apenas com os campos que foram alterados
    const patchData: FranquiaPatchData = {};
    if (formData.razao_social !== franquia.razao_social) {
      patchData.razao_social = formData.razao_social;
    }
    if (formData.nome_fantasia !== franquia.nome_fantasia) {
      patchData.nome_fantasia = formData.nome_fantasia;
    }
    if (formData.limite_funcionarios !== franquia.limite_funcionarios) {
      patchData.limite_funcionarios = formData.limite_funcionarios;
    }

    // Se nenhum dado foi alterado, não faz a requisição
    if (Object.keys(patchData).length === 0) {
      setLoading(false);
      setSuccess("Nenhuma alteração para salvar.");
      return;
    }

    try {
      await franquiaService.atualizarParcial(franquiaId, patchData);
      setSuccess("Dados da franquia atualizados com sucesso!");
      setTimeout(() => {
        navigate(`/franquias/${franquiaId}/detalhes`);
      }, 2000);
    } catch (err: any) {
      console.error('Erro ao atualizar franquia:', err);
      setError(err.response?.data?.message || 'Erro ao atualizar dados da franquia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit}>
        <div className="shadow sm:rounded-md sm:overflow-hidden">
          <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            )}
            {success && (
              <div className="rounded-md bg-green-50 p-4">
                <h3 className="text-sm font-medium text-green-800">{success}</h3>
              </div>
            )}

            <div>
              <label htmlFor="nome_fantasia" className="block text-sm font-medium text-[#29306a]">
                Nome Fantasia
              </label>
              <input
                type="text"
                name="nome_fantasia"
                id="nome_fantasia"
                maxLength={255}
                value={formData.nome_fantasia}
                onChange={handleInputChange}
                className="mt-1 focus:ring-[#32bef0] focus:border-[#32bef0] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="razao_social" className="block text-sm font-medium text-[#29306a]">
                Razão Social
              </label>
              <input
                type="text"
                name="razao_social"
                id="razao_social"
                maxLength={255}
                value={formData.razao_social}
                onChange={handleInputChange}
                className="mt-1 focus:ring-[#32bef0] focus:border-[#32bef0] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="limite_funcionarios" className="block text-sm font-medium text-[#29306a]">
                Limite de Funcionários
              </label>
              <input
                type="number"
                name="limite_funcionarios"
                id="limite_funcionarios"
                value={formData.limite_funcionarios}
                onChange={handleInputChange}
                className="mt-1 focus:ring-[#32bef0] focus:border-[#32bef0] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>

          </div>
          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <button
              type="button"
              onClick={() => navigate(`/franquias/${franquiaId}/detalhes`)}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-[#29306a] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32bef0] mr-3"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#32bef0] hover:bg-[#2ba8d8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32bef0] disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
